import React, { useCallback, useMemo, useState } from 'react';
import { Button, Input, InputWrapper, Modal } from '@components';
import { Category } from '@utilities/types';

interface CategoryCardProps {
    category: Category;
    onEdit: (category: Partial<Category>) => void;
    onViewProducts: () => void;
}

export const CategoryCard: React.FC<CategoryCardProps> = ({
  category,
  onEdit,
  onViewProducts
}) => {

    const [categoryName, setCategoryName] = useState<string>(category.name);
    const [displayEditModal, setDisplayEditModal] = useState<boolean>(false);

    const isActive = useMemo(() => category.isActive, [category]);

    const image = useMemo(()=> (
        `${process.env.REACT_APP_IMAGE_BASE_URL}/${category.images[0].fileDir}/${category.images[0].filename}`
    ), [category]);

    const onCloseModal = useCallback(()=> {
        setDisplayEditModal(false);
        setCategoryName(category.name);
    }, [category]);

    const handleUpdatedCategory = useCallback(async() => {
        if(categoryName.trim() !== '' && categoryName !== category.name) {
            await onEdit({
                ...category,
                name: categoryName,
            });
            onCloseModal();
        }
    }, [
        categoryName, category,
        onEdit, onCloseModal,
    ]);

    const onToggle = useCallback(async()=> {
        await onEdit({
            ...category,
            isActive: !isActive,
        });
    }, [category, isActive, onEdit]);

    return (
        <>
            <div className="card bg-white shadow-md rounded-lg overflow-hidden w-full max-w-sm">
                <figure className="relative">
                    <img src={image} alt={category.name} className="w-full h-40 object-cover" />
                </figure>

                <div className="p-4">
                    <h2 className="card-title text-lg font-semibold mb-2">
                        {category.name}
                    </h2>

                    <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-medium">
                            {isActive ? 'Activée' : 'Désactivée'}
                        </span>
                        <input
                            type="checkbox"
                            className="toggle toggle-primary"
                            checked={isActive}
                            onChange={onToggle}
                        />
                    </div>

                    <div className="flex flex-col gap-2 justify-between">
                        <Button onClick={()=> setDisplayEditModal(true)} className="btn btn-sm btn-secondary btn-outline w-full">
                            Modifier
                        </Button>
                        <Button onClick={onViewProducts} className="btn btn-sm btn-primary w-full">
                            Voir les produits
                        </Button>
                    </div>
                </div>
            </div>

            <Modal
                isOpen={displayEditModal}
                onClose={onCloseModal}
                title="Modifier la catégorie"
            >
                <div className='flex flex-col gap-4'>
                    <InputWrapper label='Nom de la categorie'>
                        <Input
                            placeholder="Nom de la catégorie"
                            className="input input-bordered w-full"
                            value={categoryName}
                            onChange={(e) => setCategoryName(e.target.value)}
                        />
                    </InputWrapper>
                    <Button 
                        onClick={handleUpdatedCategory} 
                        className="btn btn-success w-full"
                    >
                        Mètre à jour
                    </Button>
                </div>
            </Modal>
        </>
    );
};
